// App.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Tabs from './components/Tabs';
import SubTabs from './components/SubTabs';
import ImageProcessor from './components/ImageProcessor';
import STLGenerator from './components/STLGenerator';
import LoginOverlay from './components/LoginOverlay';
import TopBar from './components/TopBar';
import ReleaseNotesOverlay from './components/ReleaseNotesOverlay';
import PayPalOverlay from './components/PayPalOverlay';
import ThreeDKSImageProcessor from './components/KCimageProcessor';
import ThreeDKCSTLGenerator from './components/KCSTLGenerator';
import CoasterImageProcessor from './components/CoasterImageProcessor';
import CoasterSTLGenerator from './components/CoasterSTLGenerator';
import SupportTicketFormOverlay from './components/SupportTicketFormOverlay';
import MyProjects from './components/MyProjects';
import useClickNudge from './hooks/useClickNudge';
import ShopPublicPage from './components/ShopPublicPage';

// 🔥 NEW: Custom Lightbox components
import LBImageProcessor from './components/LBImageProcessor';
import LBSTLGenerator from './components/LBSTLGenerator';

// 🔥 NEW: Desk Organizer components
import DOImageProcessor from './components/DOImageProcessor';
import DOSTLGenerator from './components/DOSTLGenerator';

// 🔥 NEW: Bookmark components (case-sensitive path)
import BKMKImageProcessor from './components/BKMKimageProcessor';
import BKMKSTLGenerator from './components/BKMKSTLGenerator';

// 🔥 NEW: Cake Topper components
import CTImageProcessor from './components/CTimageProcessor';
import CTSTLGenerator from './components/CTSTLGenerator';

// 🔥 NEW: My Storefront (My Shop) single-page tab
import MyStorefront from './components/MyStorefront';

import './App.css';

// Mobile-only bottom spacer height (tabs except 11 & 12)
// TWEAK THIS VALUE:
const MOBILE_BOTTOM_SPACER_PX = 220;

const STYLES = {
  root: { display: 'flex', flexDirection: 'column', height: '100vh' },
  bodyRow: { display: 'flex', flex: 1 },
  col: { flex: 1, display: 'flex', flexDirection: 'column' },
  headerDock: { flexShrink: 0 },
  main: { flex: 1, overflow: 'auto', padding: '1rem', backgroundColor: 'white' },
  fixedOverlayFull: { position: 'fixed', inset: 0, zIndex: 9999 },

  maintenanceMask: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 9998, backgroundColor: 'rgba(255, 255, 255, 0.8)',
    display: 'flex', justifyContent: 'center', alignItems: 'center',
  },
  maintenanceImg: { maxWidth: '90vw', maxHeight: '90vh' },

  // Shared button style (ensures matching height/appearance)
  nudgeBtn: {
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid #aaa',
    background: 'rgba(255,255,255,0.92)',
    lineHeight: '18px',
    fontSize: 14,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },

  // Helper (bottom-right) — bubble above, helper row below (anchored bottom-right)
  clickNudgeWrap: {
    position: 'fixed',
    right: 20,
    bottom: 20,
    zIndex: 10000,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 10,
  },

  // NEW: the stack of the two svgs
  clickNudgeStack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 10,
  },

  // NEW: helper row = [helper svg] [x button]
  helperRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },

  // NEW: X button next to helper (NOT absolute)
  clickNudgeCloseInline: {
    width: 28,
    height: 28,
    border: 'none',
    borderRadius: '50%',
    background: 'rgba(0,0,0,0.6)',
    color: '#fff',
    fontWeight: 700,
    lineHeight: '28px',
    textAlign: 'center',
    cursor: 'pointer',
    padding: 0,
    flexShrink: 0,
  },

  // helper svg size (unchanged)
  helperImg: { display: 'block', height: 200, maxHeight: '18vmax', width: 'auto' },

  // NEW: tick bubble size (independent)
  tickCowboyImg: { display: 'block', height: 150, maxHeight: '14vmax', width: 'auto' },

  // Capitan (bottom-left) — image above, button stacked BELOW (anchored bottom-left)
  capitanWrap: {
    position: 'fixed',
    left: 20,
    bottom: 20,
    zIndex: 10000,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
  },
  capitanBox: { position: 'relative', display: 'inline-block' },
  capitanClose: {
    position: 'absolute', top: 6, left: 6, width: 24, height: 24,
    border: 'none', borderRadius: '50%',
    background: 'rgba(0,0,0,0.6)', color: '#fff',
    fontWeight: 700, lineHeight: '24px', textAlign: 'center', cursor: 'pointer', padding: 0,
  },
  capitanImg: { display: 'block', height: 200, maxHeight: '18vmax', width: 'auto' },

  paypalBtn: {
    position: 'fixed', bottom: 20, right: 20,
    padding: '12px 18px', backgroundColor: '#ffc439',
    border: 'none', borderRadius: 8,
    fontSize: '1vw', cursor: 'pointer', zIndex: 9999, fontWeight: 'bold',
  },
};

const COLOR_NAMES = [
  "Pink","Beige","Red","Deep Red","Brown","Light Brown","Yellow","Orange","Olive Green",
  "Green","Pine Green","Turquoise","Blue","Deep Blue","Light Blue","Purple","White","Black","Grey"
];

// Capitan session keys (session-only)
const CAPITAN_DISABLED_KEY = 'capitanNudgeDisabled';

function AnimatedSvg({ src, style, className, title }) {
  return (
    <object
      data={src}
      type="image/svg+xml"
      aria-label={title || 'Animated SVG'}
      className={className}
      style={{
        display: 'block',
        border: 'none',
        background: 'transparent',
        overflow: 'hidden',

        // ✅ Critical: allow wrapper div to receive click events reliably
        pointerEvents: 'none',

        ...style,
      }}
    />
  );
}

export default function App() {
  // Auth / user
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [credits, setCredits] = useState(0);
  const [tier, setTier] = useState('');
  const [maintenance, setMaintenance] = useState(false);
  const [errorState, setErrorState] = useState(false);

  // Project/session
  const [filename, setFilename] = useState('');
  const [isProcessed, setIsProcessed] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);

  // ✅ NEW: Subtab-2 navigation gate.
  const [canEnterSubTab2, setCanEnterSubTab2] = useState(false);

  // Overlays
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [showPayPal, setShowPayPal] = useState(false);
  const [showTicketForm, setShowTicketForm] = useState(false);

  // Tabs
  const [activeMainTab, setActiveMainTab] = useState(1);
  const [activeSubTab, setActiveSubTab] = useState(1);

  // Global preview layers
  const [processedImage, setProcessedImage] = useState(null);
  const [overlayImage, setOverlayImage] = useState(null);

  // Input image for hydration
  const [inputImageUrl, setInputImageUrl] = useState(null);

  // Colors & STL config
  const [colorList, setColorList] = useState([]);
  const [extrusionValues, setExtrusionValues] = useState([]);

  // Initial color panel seeds
  const [initialBaseColor, setInitialBaseColor] = useState('');
  const [initialColorList, setInitialColorList] = useState([]);

  // Init keys per product
  const [initKeySign, setInitKeySign] = useState(0);
  const [initKeyKC, setInitKeyKC] = useState(0);
  const [initKeyCoaster, setInitKeyCoaster] = useState(0);
  const [initKeyLightbox, setInitKeyLightbox] = useState(0);
  const [initKeyDesk, setInitKeyDesk] = useState(0);
  const [initKeyBookmark, setInitKeyBookmark] = useState(0);
  const [initKeyCake, setInitKeyCake] = useState(0);

  // Explicit project root so processors/STL generators can hydrate across views
  const [projectRoot, setProjectRoot] = useState(null);

  // Gate hydration to intended product (when opening from My Projects)
  const [hydrationTarget, setHydrationTarget] = useState(null);

  const [hydratedBaseSvg, setHydratedBaseSvg] = useState(null);

  // --- settings hydration from metadata.json ---
  const [initRemoveBkgrnd, setInitRemoveBkgrnd] = useState(false);
  const [initCloseBkrndHoles, setInitCloseBkrndHoles] = useState(false);
  const [initRemoveIslands, setInitRemoveIslands] = useState(false);
  const [initSpeckClean, setInitSpeckClean] = useState(false);
  const [initMkFreestanding, setInitMkFreestanding] = useState(false);
  const [initBckgrndInflate, setInitBckgrndInflate] = useState(40);

  const UPLOADS_URL_PREFIX = '/uploads';

  // ✅ Mobile-only bottom gap (tabs except 11 & 12)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth || 0;
      setIsMobile(w <= 768);
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('orientationchange', compute);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('orientationchange', compute);
    };
  }, []);

  const mainStyle = useMemo(() => {
    const needsSpacer = isMobile && activeMainTab !== 11 && activeMainTab !== 12;
    return {
      ...STYLES.main,
      paddingBottom: needsSpacer ? `${MOBILE_BOTTOM_SPACER_PX}px` : STYLES.main.paddingBottom,
    };
  }, [isMobile, activeMainTab]);

  // Derived
  const usernamePart = useMemo(() => (email || '').split('@')[0] || '', [email]);

  const kindToTab = useMemo(
    () => ({
      sign: 1,
      keychain: 2,
      coaster: 3,
      lightbox: 4,
      stand: 1,
      organizer: 5,
      bookmark: 6,
      caketopper: 7,
      standee: 1,
      other: 1
    }),
    []
  );

  // ✅ normalize tier strings robustly (hyphens/underscores/spaces/case)
  const tierNorm = useMemo(() => {
    return String(tier || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/-+/g, '_');
  }, [tier]);

  const isAdmin = useMemo(() => tierNorm === 'admin' || tierNorm.replace(/_/g, '') === 'admin', [tierNorm]);

  const isFree = useMemo(() => {
    const flat = tierNorm.replace(/_/g, '');
    return tierNorm === 'free' || flat === 'free';
  }, [tierNorm]);

  const isAllAccess = useMemo(() => {
    const flat = tierNorm.replace(/_/g, '');
    return tierNorm === 'all_access' || flat === 'allaccess';
  }, [tierNorm]);

  const isPatreonAccess = useMemo(() => {
    const flat = tierNorm.replace(/_/g, '');
    return tierNorm === 'patreon_access' || flat === 'patreonaccess';
  }, [tierNorm]);

  // ✅ Only these tiers can access Storefront + My Projects
  const canAccessStoreAndProjects = useMemo(() => {
    return isAdmin || isAllAccess || isPatreonAccess;
  }, [isAdmin, isAllAccess, isPatreonAccess]);

  // ✅ For free tier: only allow first 3 tabs (1,2,3). Lock everything else.
  // ✅ For other non-(admin/all_access/patreon_access) tiers: lock only 11,12.
  const lockedTabs = useMemo(() => {
    if (!isLoggedIn) return [];

    if (isAdmin || isAllAccess || isPatreonAccess) return [];

    if (isFree) return [4,5,6,7,8,9,10,11,12].filter(Boolean);

    return [11, 12];
  }, [isLoggedIn, isAdmin, isAllAccess, isPatreonAccess, isFree]);

  // Helper overlay (session click nudge)
  const { showClickNudge, nudgeDisabled, dismissNudge, disableNudge } = useClickNudge({
    isEnabled: isLoggedIn,
    threshold: 20,
  });

  // Ticket open handler used by BOTH helper.svg and tick_cowboy_bub.svg
  const openTicketOverlay = useCallback(() => {
    dismissNudge();
    setShowTicketForm(true);
  }, [dismissNudge]);

  // ───────────────────────────────
  // Capitan overlay state + logic
  // ───────────────────────────────
  const [capitanDisabled, setCapitanDisabled] = useState(() => {
    return sessionStorage.getItem(CAPITAN_DISABLED_KEY) === '1';
  });
  const [showCapitan, setShowCapitan] = useState(false);

  // ✅ CHANGED: use animated SVG instead of capitan.png
  const [capitanImgSrc, setCapitanImgSrc] = useState('/capitan.svg');

  // ✅ action svg state machine (App.js owns it)
  const [capitanActionSvgSrc, setCapitanActionSvgSrc] = useState('/upload.svg');

  // ✅ Unified API passed to ALL processors (keeps changes "contained" in App.js)
  // Processors MAY call these; if they don't, nothing breaks.
  const capitanApi = useMemo(() => {
    const setPreprocess = () => setCapitanActionSvgSrc('/preprocess.svg');

    return {
      // Canonical names
      onUploadStart: setPreprocess,
      onProcessStart: () => setCapitanActionSvgSrc('/process.svg'),
      onOpenAdvanced: () => setCapitanActionSvgSrc('/postprocess.svg'),
      onGoToExtrusions: () => setCapitanActionSvgSrc('/extrusions.svg'),
      onStartNewProject: () => setCapitanActionSvgSrc('/upload.svg'),

      // ✅ Backwards/alternate names your processors might be calling
      onImageUploadedToInputBox: (_payload) => setPreprocess(),
      onUploadImageClick: () => setPreprocess(),
      onUploadClick: () => setPreprocess(),
    };
  }, []);

  // Responsive SVG height: Desktop => 24vmin, Mobile => 18vmax
  const [capitanSvgHeight, setCapitanSvgHeight] = useState('24vmin');
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth || 0;
      const h = window.innerHeight || 0;
      const isMobilePortrait = w < h;
      setCapitanSvgHeight(isMobilePortrait ? '18vmax' : '24vmin');
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('orientationchange', compute);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('orientationchange', compute);
    };
  }, []);

  // ✅ Dismiss logic:
  const capitanDismissRef = useRef({ tab: null, svg: null });
  const prevMainTabRef = useRef(String(activeMainTab));

  useEffect(() => {
    const prev = prevMainTabRef.current;
    const now = String(activeMainTab);

    if (prev && prev !== now) {
      capitanDismissRef.current = { tab: null, svg: null };
    }
    prevMainTabRef.current = now;
  }, [activeMainTab]);

  // Define which main tabs have an ImageProcessor page on subtab 1
  const capitanProcessorTabs = useMemo(() => new Set(['1','2','3','4','5','6','7']), []);

  const lastCapitanShownForRef = useRef({ tab: null, sub: null });

  useEffect(() => {
    if (!isLoggedIn) return;

    // ✅ Never show Capitan on tabs 11/12, and force-hide if user navigates there
    if (activeMainTab === 11 || activeMainTab === 12) {
      if (showCapitan) setShowCapitan(false);
      return;
    }

    if (capitanDisabled) return;

    const tabKey = String(activeMainTab);
    const isProcessorTab = capitanProcessorTabs.has(tabKey);
    const onProcessorPage = isProcessorTab && activeSubTab === 1;
    if (!onProcessorPage) return;

    if (
      capitanDismissRef.current.tab === tabKey &&
      capitanDismissRef.current.svg === capitanActionSvgSrc
    ) {
      return;
    }

    if (showCapitan) return;

    const last = lastCapitanShownForRef.current;
    const changedPlace = last.tab !== tabKey || last.sub !== activeSubTab;
    if (!changedPlace) return;

    setCapitanImgSrc('/capitan.svg');
    setShowCapitan(true);
    lastCapitanShownForRef.current = { tab: tabKey, sub: activeSubTab };
  }, [
    isLoggedIn,
    capitanDisabled,
    activeMainTab,
    activeSubTab,
    capitanProcessorTabs,
    showCapitan,
    capitanActionSvgSrc,
  ]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (capitanDisabled) return;
    if (activeMainTab === 11 || activeMainTab === 12) return;

    const tabKey = String(activeMainTab);

    const wasDismissedOnThisTab =
      capitanDismissRef.current.tab === tabKey &&
      capitanDismissRef.current.svg !== null;

    const svgChangedSinceDismiss =
      wasDismissedOnThisTab &&
      capitanDismissRef.current.svg !== capitanActionSvgSrc;

    if (svgChangedSinceDismiss) {
      capitanDismissRef.current = { tab: null, svg: null };
      setCapitanImgSrc('/capitan.svg');
      setShowCapitan(true);
    }
  }, [capitanActionSvgSrc, isLoggedIn, capitanDisabled, activeMainTab]);

  const dismissCapitan = useCallback(() => {
    capitanDismissRef.current = { tab: String(activeMainTab), svg: capitanActionSvgSrc };
    setShowCapitan(false);
  }, [activeMainTab, capitanActionSvgSrc]);

  const disableCapitan = useCallback(() => {
    sessionStorage.setItem(CAPITAN_DISABLED_KEY, '1');
    setCapitanDisabled(true);
    setShowCapitan(false);
  }, []);

  // ✅ If user is on a locked tab and loses access, kick them out
  useEffect(() => {
    if (!isLoggedIn) return;
    if (lockedTabs.includes(activeMainTab)) {
      setActiveMainTab(1);
      setActiveSubTab(1);
    }
  }, [isLoggedIn, lockedTabs, activeMainTab]);

  // ✅ Subtab-2 navigation rule:
  useEffect(() => {
    if (activeSubTab === 2 && !canEnterSubTab2) setActiveSubTab(1);
  }, [activeSubTab, canEnterSubTab2]);

  const prevSubTabRef = useRef(activeSubTab);
  useEffect(() => {
    prevSubTabRef.current = activeSubTab;
  }, [activeSubTab]);

  const setActiveSubTabGuarded = useCallback((next) => {
    if (next === 2 && !canEnterSubTab2) return;

    const prev = prevSubTabRef.current;
    if (next === prev) return;

    setActiveSubTab(next);

    if (next === 2) {
      capitanApi.onGoToExtrusions();
    } else if (next === 1 && prev === 2) {
      capitanApi.onOpenAdvanced();
    }
  }, [canEnterSubTab2, capitanApi]);

  useEffect(() => {
    const processingDone = Boolean(filename) && Boolean(processedImage) && Boolean(overlayImage);
    setIsProcessed(processingDone);
    if (processingDone) setCanEnterSubTab2(true);
  }, [filename, processedImage, overlayImage]);

  // ✅ Smooth scroll when main or subtab changes (excluding My Projects + My Storefront)
  useEffect(() => {
    if (activeMainTab !== 12 && activeMainTab !== 11) {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  }, [activeMainTab, activeSubTab]);

  // ✅ For My Storefront (tab 11), scroll only once when the tab is loaded
  useEffect(() => {
    if (activeMainTab === 11) {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  }, [activeMainTab]);

  useEffect(() => {
    if (activeSubTab !== 1 && activeSubTab !== 2) setActiveSubTab(1);
  }, [activeMainTab, activeSubTab]);

  const safeFetchJson = useCallback(async (url, opts = {}) => {
    const r = await fetch(url, { cache: 'no-store', ...opts });
    if (!r.ok) throw new Error(`Fetch failed ${r.status}: ${url}`);
    return r.json();
  }, []);

  const fetchMaintenanceStatus = useCallback(async () => {
    try {
      const data = await safeFetchJson('/maintenance-status');
      setMaintenance(data.maintenance === true);
    } catch (err) {
      console.error('Error checking maintenance status:', err);
    }
  }, [safeFetchJson]);

  const handleToggleMaintenance = useCallback(async () => {
    try {
      const data = await safeFetchJson('/toggle-maintenance/', { method: 'POST' });
      setMaintenance(Boolean(data.maintenance));
    } catch (err) {
      console.error('Failed to toggle maintenance mode:', err);
    }
  }, [safeFetchJson]);

  const urlExists = useCallback(async (url) => {
    try {
      let r = await fetch(url, { method: 'HEAD', cache: 'no-store' });
      if (r.ok) return true;
      if (r.status === 405 || r.status === 501) {
        r = await fetch(url, { method: 'GET', cache: 'no-store' });
        return r.ok;
      }
      return false;
    } catch {
      try {
        const r2 = await fetch(url, { method: 'GET', cache: 'no-store' });
        return r2.ok;
      } catch {
        return false;
      }
    }
  }, []);

  const extractColorsFromMeta = useCallback((meta) => {
    if (!meta || typeof meta !== 'object') return [];
    const nameFromIndexedFile = (p) => {
      if (!p || typeof p === 'string') {
        const m = String(p || '').match(/(?:^|\/)\s*\d+\s*-\s*([^./]+)\.(?:svg|png)$/i);
        return m?.[1]?.trim() || '';
      }
      return '';
    };
    const SNAP = (c) => {
      if (!c) return '';
      const found = COLOR_NAMES.find(n => n.toLowerCase() === String(c).toLowerCase());
      return found || c;
    };

    let base = nameFromIndexedFile(meta.base_svg);
    let rest = [];

    if (Array.isArray(meta.colors)) {
      const objectEntries = meta.colors
        .filter((x) => x && typeof x === 'object')
        .map((x) => {
          const byFile = nameFromIndexedFile(x.file);
          const byName = (x.name || '').toString().trim();
          return SNAP(byFile || byName);
        })
        .filter(Boolean);

      const stringEntries = meta.colors
        .filter((x) => typeof x === 'string')
        .map((s) => SNAP(s.trim()))
        .filter(Boolean);

      rest = [...objectEntries, ...stringEntries];
    }

    if (!base && Array.isArray(meta.colors)) {
      const baseObj = meta.colors.find((c) => String(c?.name).toLowerCase() === 'base');
      if (baseObj) {
        base = SNAP(nameFromIndexedFile(baseObj.file) || String(baseObj.name || '').trim());
      }
    }

    if (!base || rest.length === 0) {
      const fallbackList =
        (Array.isArray(meta.color_names) && meta.color_names) ||
        (Array.isArray(meta.selected_colors) && meta.selected_colors) ||
        [];
      const snapped = fallbackList.map((s) => SNAP(String(s || '').trim())).filter(Boolean);
      if (!base && snapped.length) base = snapped[0];
      if (rest.length === 0 && snapped.length > 1) rest = snapped.slice(1);
    }

    const baseLower = (base || '').toLowerCase();
    rest = rest.filter((c) => c && c.toLowerCase() !== baseLower);
    return [base, ...rest].filter(Boolean);
  }, []);

  const deriveKind = (proj) => {
    if (proj?.kind && typeof proj.kind === 'string') return proj.kind.toLowerCase();
    if (proj?.id && proj.id.includes('-')) return proj.id.split('-')[0].toLowerCase();
    return 'sign';
  };

  const normalizeKind = (k) => {
    const t = (k || '').toLowerCase().trim();
    if (['sign', '3dsign', 'custom', 'signs'].includes(t)) return 'sign';
    if (['keychain', '3dkc', 'kc', 'keychains'].includes(t)) return 'keychain';
    if (['coaster', 'coasters'].includes(t)) return 'coaster';
    if (['stand', 'phone-stand', 'phone stand'].includes(t)) return 'stand';
    if (['lightbox', 'light-box', 'light box'].includes(t)) return 'lightbox';
    if (['organizer','desk-organizer','desk organizer','deskorganizer','desk_org','desk_orgs'].includes(t)) return 'organizer';
    if (['bookmark','bookmarks','bkmk','bkmks'].includes(t)) return 'bookmark';
    if (['cake topper','caketopper','cake-topper','cake_topper','cake toppers','cake-toppers','cake_toppers'].includes(t)) return 'caketopper';
    if (['standee','figure','figurine'].includes(t)) return 'standee';
    return 'sign';
  };

  // Reset when switching product tabs / starting a new product project
  const resetAllForNewTab = useCallback(() => {
    setFilename('');
    setIsProcessed(false);
    setHasDownloaded(false);
    setProcessedImage(null);
    setOverlayImage(null);
    setInputImageUrl(null);
    setInitialBaseColor('');
    setInitialColorList([]);
    setColorList([]);
    setExtrusionValues([]);
    setProjectRoot(null);
    setHydrationTarget(null);
    setHydratedBaseSvg(null);

    // ✅ critical: subtab2 must start disabled for a new project
    setCanEnterSubTab2(false);

    setInitRemoveBkgrnd(false);
    setInitCloseBkrndHoles(false);
    setInitRemoveIslands(false);
    setInitSpeckClean(false);
    setInitMkFreestanding(false);
    setInitBckgrndInflate(40);

    setInitKeySign(n => n + 1);
    setInitKeyKC(n => n + 1);
    setInitKeyCoaster(n => n + 1);
    setInitKeyLightbox(n => n + 1);
    setInitKeyDesk(n => n + 1);
    setInitKeyBookmark(n => n + 1);
    setInitKeyCake(n => n + 1);

    setActiveSubTab(1);

    // reset capitan action svg baseline when switching products
    capitanApi.onStartNewProject();
  }, [capitanApi]);

  // ✅ Main tab change handler
  const handleMainTabChange = useCallback((newTabIndex) => {
    if (lockedTabs.includes(newTabIndex)) return;

    setCanEnterSubTab2(false);
    setActiveSubTab(1);

    if (newTabIndex !== 12) {
      resetAllForNewTab();
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
    setActiveMainTab(newTabIndex);
  }, [resetAllForNewTab, lockedTabs]);

  const handleOpenProject = useCallback(async (project) => {
    try {
      await fetch("/log-open-project/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          project_id: project?.id,
          slug: project?.slug || null,
        }),
      });
    } catch (err) {
      console.warn("Failed to log project open:", err);
    }

    const k = normalizeKind(deriveKind(project));
    const targetTab = kindToTab[k] || 1;

    setActiveMainTab(targetTab);
    setActiveSubTab(1);

    setCanEnterSubTab2(true);

    const slug = (project?.slug || '').trim();
    const root = `/outputs/${usernamePart}/${project.id}`;
    setProjectRoot(root);
    setHydrationTarget(k);

    let nextFilename = '';
    let nextInputUrl = null;
    let meta = {};
    try {
      const r = await fetch(`${root}/metadata.json`, { cache: 'no-store' });
      if (r.ok) meta = await r.json();
    } catch {
      console.warn('No or invalid metadata.json for project', project?.id);
    }

    try {
      const s = meta?.settings || {};
      const toBool = (v, fallback = false) => {
        if (typeof v === 'boolean') return v;
        if (typeof v === 'number') return v !== 0;
        if (typeof v === 'string') {
          const t = v.trim().toLowerCase();
          if (['true','1','yes','y','on'].includes(t)) return true;
          if (['false','0','no','n','off'].includes(t)) return false;
        }
        return fallback;
      };
      const toInt = (v, fallback = 40) => {
        const n = parseInt(v, 10);
        return Number.isFinite(n) ? n : fallback;
      };

      setInitRemoveBkgrnd(toBool(s.removeBkgrnd, false));
      setInitCloseBkrndHoles(toBool(s.closeBkrndHoles, false));
      setInitRemoveIslands(toBool(s.removeIslands, false));
      setInitSpeckClean(toBool(s.speckClean, false));
      setInitMkFreestanding(toBool(s.mkFreestanding, false));
      setInitBckgrndInflate(toInt(s.bckgrndInflate, 40));
    } catch (e) {
      console.warn('Failed to hydrate settings from metadata.json:', e);
      setInitRemoveBkgrnd(false);
      setInitCloseBkrndHoles(false);
      setInitRemoveIslands(false);
      setInitSpeckClean(false);
      setInitMkFreestanding(false);
      setInitBckgrndInflate(40);
    }

    const ts = `?t=${Date.now()}`;
    let sourceNameWithExt = meta?.upload_name || meta?.source || meta?.name || null;

    if (sourceNameWithExt) {
      const uploadUrl = `${UPLOADS_URL_PREFIX}/${sourceNameWithExt}`;
      if (await urlExists(uploadUrl)) {
        nextInputUrl = `${uploadUrl}${ts}`;
        nextFilename = sourceNameWithExt;
      }
    }

    if (!nextFilename) {
      const candidates = [
        `${UPLOADS_URL_PREFIX}/${slug}.png`,
        `${UPLOADS_URL_PREFIX}/${slug}.jpg`,
        `${UPLOADS_URL_PREFIX}/${slug}.jpeg`,
      ];
      for (const u of candidates) {
        if (await urlExists(u)) {
          nextInputUrl = `${u}${ts}`;
          nextFilename = (u.split('/').pop() || '').trim();
          break;
        }
      }
    }

    if (!nextFilename) {
      const synthBase = String(project?.id || 'untitled').replace(/^output-/, '').trim() || 'untitled';
      nextFilename = `${synthBase}.png`;
    }

    setFilename(nextFilename);

    setIsProcessed(false);
    setHasDownloaded(false);
    setProcessedImage(null);
    setOverlayImage(null);
    setInputImageUrl(nextInputUrl);

    const effectiveBaseSvg =
      (k === 'bookmark' || k === 'caketopper')
        ? 'temp/bookmarked.svg'
        : (meta?.base_svg || null);

    setHydratedBaseSvg(effectiveBaseSvg);

    const metaForColors = effectiveBaseSvg ? { ...meta, base_svg: effectiveBaseSvg } : meta;

    const colors = extractColorsFromMeta(metaForColors)
      .map(c => (typeof c === 'string' ? c.trim() : ''))
      .filter(Boolean);

    if (colors.length >= 2) {
      setInitialBaseColor(colors[0]);
      setInitialColorList(colors.slice(1));
    } else {
      setInitialBaseColor('');
      setInitialColorList([]);
    }

    if (k === 'sign') setInitKeySign(n => n + 1);
    if (k === 'keychain') setInitKeyKC(n => n + 1);
    if (k === 'coaster') setInitKeyCoaster(n => n + 1);
    if (k === 'lightbox') setInitKeyLightbox(n => n + 1);
    if (k === 'organizer') setInitKeyDesk(n => n + 1);
    if (k === 'bookmark') setInitKeyBookmark(n => n + 1);
    if (k === 'caketopper') setInitKeyCake(n => n + 1);

    capitanApi.onUploadStart();
  }, [urlExists, usernamePart, kindToTab, email, extractColorsFromMeta, capitanApi]);

  const noop = useCallback(() => {}, []);

  // ✅ App-level trigger for preprocess.svg when user clicks the upload image box
  useEffect(() => {
    const handler = (e) => {
      const el = e.target;
      if (el?.tagName === 'INPUT' && el?.type === 'file') {
        capitanApi.onUploadStart();
      }
    };
    document.addEventListener('click', handler, true);
    return () => {
      document.removeEventListener('click', handler, true);
    };
  }, [capitanApi]);

  // ✅ gates are OPEN by default when hydrationTarget is null.
  const gateSign = !hydrationTarget || hydrationTarget === 'sign';
  const gateKC = !hydrationTarget || hydrationTarget === 'keychain';
  const gateCoaster = !hydrationTarget || hydrationTarget === 'coaster';
  const gateLightbox = !hydrationTarget || hydrationTarget === 'lightbox';
  const gateDesk = !hydrationTarget || hydrationTarget === 'organizer';
  const gateBookmark = !hydrationTarget || hydrationTarget === 'bookmark';
  const gateCake = !hydrationTarget || hydrationTarget === 'caketopper';

  // ───────────────────────────────
  // Public shop route: /shop/<slug>
  // (NO hooks after this return decision; all hooks are above)
  // ───────────────────────────────
  const path = window.location.pathname;
  const isShopRoute = path.startsWith('/shop/');
  const shopSlug = isShopRoute ? decodeURIComponent(path.slice('/shop/'.length)) : '';
  if (isShopRoute) {
    return <ShopPublicPage shopSlug={shopSlug} />;
  }

  return (
    <div style={STYLES.root}>
      {isLoggedIn && (
        <TopBar
          key={`topbar-${credits}`}
          email={email}
          username={email}
          tier={tier}
          credits={credits}
          setCredits={setCredits}
          insufficientCredits={credits === 0}
          handleClearCache={noop}
          handleToggleMaintenance={handleToggleMaintenance}
          maintenance={maintenance}
        />
      )}

      <div style={STYLES.bodyRow}>
        <div style={STYLES.col}>
          <div style={STYLES.headerDock}>
            <Tabs
              activeTab={activeMainTab}
              setActiveTab={handleMainTabChange}
              tabsLocked={lockedTabs.length > 0}
              lockedFor={lockedTabs}
            />
            <SubTabs
              activeMainTab={activeMainTab}
              activeSubTab={activeSubTab}
              setActiveSubTab={setActiveSubTabGuarded}
              isProcessed={canEnterSubTab2}
              hasDownloaded={hasDownloaded}
              tabsLocked={false}
            />
          </div>

          <main style={mainStyle}>
            {/* --- SIGN --- */}
            {activeMainTab === 1 && (
              <div style={{ display: activeSubTab === 1 ? 'block' : 'none' }} aria-hidden={activeSubTab !== 1}>
                <ImageProcessor
                  filename={filename}
                  setFilename={setFilename}
                  isProcessed={isProcessed}
                  setIsProcessed={setIsProcessed}
                  email={email}
                  setErrorState={setErrorState}
                  errorState={errorState}
                  credits={credits}
                  processedImage={gateSign ? processedImage : null}
                  setProcessedImage={setProcessedImage}
                  overlayImage={gateSign ? overlayImage : null}
                  setOverlayImage={setOverlayImage}
                  setColorList={setColorList}
                  setExtrusionValues={setExtrusionValues}
                  inputImageUrl={gateSign ? inputImageUrl : null}
                  initialBaseColor={gateSign ? initialBaseColor : ''}
                  initialColorList={gateSign ? initialColorList : []}
                  hydrateGate={gateSign}
                  initAdvancedPanelKey={initKeySign}
                  projectRoot={gateSign ? projectRoot : null}
                  initialRemoveBkgrnd={gateSign ? initRemoveBkgrnd : false}
                  initialCloseBkrndHoles={gateSign ? initCloseBkrndHoles : false}
                  initialRemoveIslands={gateSign ? initRemoveIslands : false}
                  initialSpeckClean={gateSign ? initSpeckClean : false}
                  initialMkFreestanding={gateSign ? initMkFreestanding : false}
                  initialBckgrndInflate={gateSign ? initBckgrndInflate : 40}
                  capitanApi={capitanApi}
                  onUploadImageClick={() => {
                    capitanApi.onUploadStart();
                    setCanEnterSubTab2(false);

                    setHydrationTarget(null);
                    setHydratedBaseSvg(null);

                    setProcessedImage(null);
                    setOverlayImage(null);
                    setHasDownloaded(false);
                    setActiveSubTab(1);
                  }}
                  onProcessImageClick={() => capitanApi.onProcessStart()}
                  onOpenAdvancedControls={() => capitanApi.onOpenAdvanced()}
                  onStartNewProject={() => {
                    capitanApi.onStartNewProject();
                    setCanEnterSubTab2(false);

                    setInputImageUrl(null);
                    setHydrationTarget(null);
                    setProjectRoot(null);
                    setHydratedBaseSvg(null);

                    setProcessedImage(null);
                    setOverlayImage(null);
                    setHasDownloaded(false);
                    setActiveSubTab(1);
                  }}
                />
              </div>
            )}
            {activeMainTab === 1 && activeSubTab === 2 && (
              <STLGenerator
                filename={filename}
                email={email}
                credits={credits}
                setCredits={setCredits}
                isProcessed={isProcessed}
                processedImage={processedImage}
                overlayImage={overlayImage}
                colorList={colorList}
                extrusionValues={extrusionValues}
                setExtrusionValues={setExtrusionValues}
                projectRoot={projectRoot}
                setHasDownloaded={setHasDownloaded}
              />
            )}

            {/* --- KEYCHAIN --- */}
            {activeMainTab === 2 && (
              <div style={{ display: activeSubTab === 1 ? 'block' : 'none' }} aria-hidden={activeSubTab !== 1}>
                <ThreeDKSImageProcessor
                  filename={filename}
                  setFilename={setFilename}
                  setIsProcessed={setIsProcessed}
                  email={email}
                  setErrorState={setErrorState}
                  errorState={errorState}
                  credits={credits}
                  processedImage={gateKC ? processedImage : null}
                  setProcessedImage={setProcessedImage}
                  overlayImage={gateKC ? overlayImage : null}
                  setOverlayImage={setOverlayImage}
                  setColorList={setColorList}
                  inputImageUrl={gateKC ? inputImageUrl : null}
                  initialBaseColor={gateKC ? initialBaseColor : ''}
                  initialColorList={gateKC ? initialColorList : []}
                  hydrateGate={gateKC}
                  initAdvancedPanelKey={initKeyKC}
                  projectRoot={gateKC ? projectRoot : null}
                  initialRemoveBkgrnd={gateKC ? initRemoveBkgrnd : false}
                  initialCloseBkrndHoles={gateKC ? initCloseBkrndHoles : false}
                  initialRemoveIslands={gateKC ? initRemoveIslands : false}
                  initialSpeckClean={gateKC ? initSpeckClean : false}
                  initialMkFreestanding={gateKC ? initMkFreestanding : false}
                  initialBckgrndInflate={gateKC ? initBckgrndInflate : 40}
                  capitanApi={capitanApi}
                  onUploadImageClick={() => {
                    capitanApi.onUploadStart();
                    setCanEnterSubTab2(false);

                    setHydrationTarget(null);
                    setProcessedImage(null);
                    setOverlayImage(null);
                    setHasDownloaded(false);
                    setActiveSubTab(1);
                  }}
                  onProcessImageClick={() => capitanApi.onProcessStart()}
                  onOpenAdvancedControls={() => capitanApi.onOpenAdvanced()}
                  onStartNewProject={() => {
                    capitanApi.onStartNewProject();
                    setCanEnterSubTab2(false);

                    setInputImageUrl(null);
                    setHydrationTarget(null);
                    setProjectRoot(null);

                    setProcessedImage(null);
                    setOverlayImage(null);
                    setHasDownloaded(false);
                    setActiveSubTab(1);
                  }}
                />
              </div>
            )}
            {activeMainTab === 2 && activeSubTab === 2 && (
              <ThreeDKCSTLGenerator
                filename={filename}
                email={email}
                credits={credits}
                setCredits={setCredits}
                isProcessed={isProcessed}
                processedImage={processedImage}
                overlayImage={overlayImage}
                colorList={colorList}
                extrusionValues={extrusionValues}
                setExtrusionValues={setExtrusionValues}
                projectRoot={projectRoot}
                setHasDownloaded={setHasDownloaded}
              />
            )}

            {/* --- COASTER --- */}
            {activeMainTab === 3 && (
              <div style={{ display: activeSubTab === 1 ? 'block' : 'none' }} aria-hidden={activeSubTab !== 1}>
                <CoasterImageProcessor
                  filename={filename}
                  setFilename={setFilename}
                  setIsProcessed={setIsProcessed}
                  email={email}
                  setErrorState={setErrorState}
                  errorState={errorState}
                  credits={credits}
                  processedImage={gateCoaster ? processedImage : null}
                  setProcessedImage={setProcessedImage}
                  overlayImage={gateCoaster ? overlayImage : null}
                  setOverlayImage={setOverlayImage}
                  setColorList={setColorList}
                  setExtrusionValues={setExtrusionValues}
                  inputImageUrl={gateCoaster ? inputImageUrl : null}
                  initialBaseColor={gateCoaster ? initialBaseColor : ''}
                  initialColorList={gateCoaster ? initialColorList : []}
                  hydrateGate={gateCoaster}
                  initAdvancedPanelKey={initKeyCoaster}
                  projectRoot={gateCoaster ? projectRoot : null}
                  initialRemoveBkgrnd={gateCoaster ? initRemoveBkgrnd : false}
                  initialCloseBkrndHoles={gateCoaster ? initCloseBkrndHoles : false}
                  initialRemoveIslands={gateCoaster ? initRemoveIslands : false}
                  initialSpeckClean={gateCoaster ? initSpeckClean : false}
                  initialMkFreestanding={gateCoaster ? initMkFreestanding : false}
                  initialBckgrndInflate={gateCoaster ? initBckgrndInflate : 40}
                  capitanApi={capitanApi}
                  onUploadImageClick={() => {
                    capitanApi.onUploadStart();
                    setCanEnterSubTab2(false);

                    setHydrationTarget(null);
                    setProcessedImage(null);
                    setOverlayImage(null);
                    setHasDownloaded(false);
                    setActiveSubTab(1);
                  }}
                  onProcessImageClick={() => capitanApi.onProcessStart()}
                  onOpenAdvancedControls={() => capitanApi.onOpenAdvanced()}
                  onStartNewProject={() => {
                    capitanApi.onStartNewProject();
                    setCanEnterSubTab2(false);

                    setInputImageUrl(null);
                    setHydrationTarget(null);
                    setProjectRoot(null);

                    setProcessedImage(null);
                    setOverlayImage(null);
                    setHasDownloaded(false);
                    setActiveSubTab(1);
                  }}
                />
              </div>
            )}
            {activeMainTab === 3 && activeSubTab === 2 && (
              <CoasterSTLGenerator
                filename={filename}
                email={email}
                credits={credits}
                setCredits={setCredits}
                isProcessed={isProcessed}
                processedImage={processedImage}
                overlayImage={overlayImage}
                colorList={colorList}
                extrusionValues={extrusionValues}
                setExtrusionValues={setExtrusionValues}
                projectRoot={projectRoot}
                setHasDownloaded={setHasDownloaded}
              />
            )}

            {/* --- CUSTOM LIGHTBOX (Tab 4) --- */}
            {activeMainTab === 4 && !lockedTabs.includes(4) && (
              <div style={{ display: activeSubTab === 1 ? 'block' : 'none' }} aria-hidden={activeSubTab !== 1}>
                <LBImageProcessor
                  filename={filename}
                  setFilename={setFilename}
                  isProcessed={isProcessed}
                  setIsProcessed={setIsProcessed}
                  email={email}
                  setErrorState={setErrorState}
                  errorState={errorState}
                  credits={credits}
                  processedImage={gateLightbox ? processedImage : null}
                  setProcessedImage={setProcessedImage}
                  overlayImage={gateLightbox ? overlayImage : null}
                  setOverlayImage={setOverlayImage}
                  setColorList={setColorList}
                  setExtrusionValues={setExtrusionValues}
                  inputImageUrl={gateLightbox ? inputImageUrl : null}
                  initialBaseColor={gateLightbox ? initialBaseColor : ''}
                  initialColorList={gateLightbox ? initialColorList : []}
                  hydrateGate={gateLightbox}
                  initAdvancedPanelKey={initKeyLightbox}
                  projectRoot={gateLightbox ? projectRoot : null}
                  initialRemoveBkgrnd={gateLightbox ? initRemoveBkgrnd : false}
                  initialCloseBkrndHoles={gateLightbox ? initCloseBkrndHoles : false}
                  initialRemoveIslands={gateLightbox ? initRemoveIslands : false}
                  initialSpeckClean={gateLightbox ? initSpeckClean : false}
                  initialMkFreestanding={gateLightbox ? initMkFreestanding : false}
                  initialBckgrndInflate={gateLightbox ? initBckgrndInflate : 40}
                  capitanApi={capitanApi}
                  onUploadImageClick={() => {
                    capitanApi.onUploadStart();
                    setCanEnterSubTab2(false);

                    setHydrationTarget(null);
                    setProcessedImage(null);
                    setOverlayImage(null);
                    setHasDownloaded(false);
                    setActiveSubTab(1);
                  }}
                  onProcessImageClick={() => capitanApi.onProcessStart()}
                  onOpenAdvancedControls={() => capitanApi.onOpenAdvanced()}
                  onStartNewProject={() => {
                    capitanApi.onStartNewProject();
                    setCanEnterSubTab2(false);

                    setInputImageUrl(null);
                    setHydrationTarget(null);
                    setProjectRoot(null);

                    setProcessedImage(null);
                    setOverlayImage(null);
                    setHasDownloaded(false);
                    setActiveSubTab(1);
                  }}
                />
              </div>
            )}
            {activeMainTab === 4 && activeSubTab === 2 && !lockedTabs.includes(4) && (
              <LBSTLGenerator
                filename={filename}
                email={email}
                credits={credits}
                setCredits={setCredits}
                isProcessed={isProcessed}
                processedImage={processedImage}
                overlayImage={overlayImage}
                colorList={colorList}
                extrusionValues={extrusionValues}
                setExtrusionValues={setExtrusionValues}
                projectRoot={projectRoot}
                setHasDownloaded={setHasDownloaded}
              />
            )}

            {/* --- DESK ORGANIZER (Tab 5) --- */}
            {activeMainTab === 5 && !lockedTabs.includes(5) && (
              <div style={{ display: activeSubTab === 1 ? 'block' : 'none' }} aria-hidden={activeSubTab !== 1}>
                <DOImageProcessor
                  filename={filename}
                  setFilename={setFilename}
                  isProcessed={isProcessed}
                  setIsProcessed={setIsProcessed}
                  email={email}
                  setErrorState={setErrorState}
                  errorState={errorState}
                  credits={credits}
                  processedImage={gateDesk ? processedImage : null}
                  setProcessedImage={setProcessedImage}
                  overlayImage={gateDesk ? overlayImage : null}
                  setOverlayImage={setOverlayImage}
                  setColorListExternal={setColorList}
                  inputImageUrl={gateDesk ? inputImageUrl : null}
                  initialBaseColor={gateDesk ? initialBaseColor : ''}
                  initialColorList={gateDesk ? initialColorList : []}
                  hydrateGate={gateDesk}
                  initAdvancedPanelKey={initKeyDesk}
                  projectRoot={gateDesk ? projectRoot : null}
                  initialRemoveBkgrnd={gateDesk ? initRemoveBkgrnd : false}
                  initialCloseBkrndHoles={gateDesk ? initCloseBkrndHoles : false}
                  initialRemoveIslands={gateDesk ? initRemoveIslands : false}
                  initialSpeckClean={gateDesk ? initSpeckClean : false}
                  initialMkFreestanding={gateDesk ? initMkFreestanding : false}
                  initialBckgrndInflate={gateDesk ? initBckgrndInflate : 40}
                  capitanApi={capitanApi}
                  onUploadImageClick={() => {
                    capitanApi.onUploadStart();
                    setCanEnterSubTab2(false);
                  }}
                  onProcessImageClick={() => capitanApi.onProcessStart()}
                  onOpenAdvancedControls={() => capitanApi.onOpenAdvanced()}
                  onStartNewProject={() => {
                    capitanApi.onStartNewProject();
                    setCanEnterSubTab2(false);

                    setInputImageUrl(null);
                    setHydrationTarget(null);
                    setProjectRoot(null);

                    setProcessedImage(null);
                    setOverlayImage(null);
                    setHasDownloaded(false);
                    setActiveSubTab(1);
                  }}
                />
              </div>
            )}
            {activeMainTab === 5 && activeSubTab === 2 && !lockedTabs.includes(5) && (
              <DOSTLGenerator
                filename={filename}
                email={email}
                credits={credits}
                setCredits={setCredits}
                isProcessed={isProcessed}
                processedImage={processedImage}
                overlayImage={overlayImage}
                colorList={colorList}
                extrusionValues={extrusionValues}
                setExtrusionValues={setExtrusionValues}
                projectRoot={projectRoot}
                setHasDownloaded={setHasDownloaded}
              />
            )}

            {/* --- BOOKMARK (Tab 6) --- */}
            {activeMainTab === 6 && !lockedTabs.includes(6) && (
              <div style={{ display: activeSubTab === 1 ? 'block' : 'none' }} aria-hidden={activeSubTab !== 1}>
                <BKMKImageProcessor
                  filename={filename}
                  setFilename={setFilename}
                  isProcessed={isProcessed}
                  setIsProcessed={setIsProcessed}
                  email={email}
                  setErrorState={setErrorState}
                  errorState={errorState}
                  credits={credits}
                  processedImage={gateBookmark ? processedImage : null}
                  setProcessedImage={setProcessedImage}
                  overlayImage={gateBookmark ? overlayImage : null}
                  setOverlayImage={setOverlayImage}
                  setColorList={setColorList}
                  setExtrusionValues={setExtrusionValues}
                  inputImageUrl={gateBookmark ? inputImageUrl : null}
                  initialBaseColor={gateBookmark ? initialBaseColor : ''}
                  initialColorList={gateBookmark ? initialColorList : []}
                  hydrateGate={gateBookmark}
                  initAdvancedPanelKey={initKeyBookmark}
                  projectRoot={gateBookmark ? projectRoot : null}
                  initialRemoveBkgrnd={gateBookmark ? initRemoveBkgrnd : false}
                  initialCloseBkrndHoles={gateBookmark ? initCloseBkrndHoles : false}
                  initialRemoveIslands={gateBookmark ? initRemoveIslands : false}
                  initialSpeckClean={gateBookmark ? initSpeckClean : false}
                  initialMkFreestanding={gateBookmark ? initMkFreestanding : false}
                  initialBckgrndInflate={gateBookmark ? initBckgrndInflate : 40}
                  hydratedBaseSvg={gateBookmark ? hydratedBaseSvg : null}
                  capitanApi={capitanApi}
                  onUploadImageClick={() => {
                    capitanApi.onUploadStart();
                    setCanEnterSubTab2(false);

                    setHydrationTarget(null);
                    setProcessedImage(null);
                    setOverlayImage(null);
                    setHasDownloaded(false);
                    setActiveSubTab(1);
                  }}
                  onProcessImageClick={() => capitanApi.onProcessStart()}
                  onOpenAdvancedControls={() => capitanApi.onOpenAdvanced()}
                  onStartNewProject={() => {
                    capitanApi.onStartNewProject();
                    setCanEnterSubTab2(false);

                    setInputImageUrl(null);
                    setHydrationTarget(null);
                    setProjectRoot(null);

                    setProcessedImage(null);
                    setOverlayImage(null);
                    setHasDownloaded(false);
                    setActiveSubTab(1);
                  }}
                />
              </div>
            )}
            {activeMainTab === 6 && activeSubTab === 2 && !lockedTabs.includes(6) && (
              <BKMKSTLGenerator
                filename={filename}
                email={email}
                credits={credits}
                setCredits={setCredits}
                isProcessed={isProcessed}
                processedImage={processedImage}
                overlayImage={overlayImage}
                colorList={colorList}
                extrusionValues={extrusionValues}
                setExtrusionValues={setExtrusionValues}
                projectRoot={projectRoot}
                setHasDownloaded={setHasDownloaded}
                hydratedBaseSvg={hydratedBaseSvg}
              />
            )}

            {/* --- CAKE TOPPER (Tab 7) --- */}
            {activeMainTab === 7 && !lockedTabs.includes(7) && (
              <div style={{ display: activeSubTab === 1 ? 'block' : 'none' }} aria-hidden={activeSubTab !== 1}>
                <CTImageProcessor
                  filename={filename}
                  setFilename={setFilename}
                  isProcessed={isProcessed}
                  setIsProcessed={setIsProcessed}
                  email={email}
                  setErrorState={setErrorState}
                  errorState={errorState}
                  credits={credits}
                  processedImage={gateCake ? processedImage : null}
                  setProcessedImage={setProcessedImage}
                  overlayImage={gateCake ? overlayImage : null}
                  setOverlayImage={setOverlayImage}
                  setColorList={setColorList}
                  setExtrusionValues={setExtrusionValues}
                  inputImageUrl={gateCake ? inputImageUrl : null}
                  initialBaseColor={gateCake ? initialBaseColor : ''}
                  initialColorList={gateCake ? initialColorList : []}
                  hydrateGate={gateCake}
                  initAdvancedPanelKey={initKeyCake}
                  projectRoot={gateCake ? projectRoot : null}
                  initialRemoveBkgrnd={gateCake ? initRemoveBkgrnd : false}
                  initialCloseBkrndHoles={gateCake ? initCloseBkrndHoles : false}
                  initialRemoveIslands={gateCake ? initRemoveIslands : false}
                  initialSpeckClean={gateCake ? initSpeckClean : false}
                  initialMkFreestanding={gateCake ? initMkFreestanding : false}
                  initialBckgrndInflate={gateCake ? initBckgrndInflate : 40}
                  hydratedBaseSvg={gateCake ? hydratedBaseSvg : null}
                  capitanApi={capitanApi}
                  onUploadImageClick={() => {
                    capitanApi.onUploadStart();
                    setCanEnterSubTab2(false);

                    setHydrationTarget(null);
                    setProcessedImage(null);
                    setOverlayImage(null);
                    setHasDownloaded(false);
                    setActiveSubTab(1);
                  }}
                  onProcessImageClick={() => capitanApi.onProcessStart()}
                  onOpenAdvancedControls={() => capitanApi.onOpenAdvanced()}
                  onStartNewProject={() => {
                    capitanApi.onStartNewProject();
                    setCanEnterSubTab2(false);

                    setInputImageUrl(null);
                    setHydrationTarget(null);
                    setProjectRoot(null);

                    setProcessedImage(null);
                    setOverlayImage(null);
                    setHasDownloaded(false);
                    setActiveSubTab(1);
                  }}
                />
              </div>
            )}
            {activeMainTab === 7 && activeSubTab === 2 && !lockedTabs.includes(7) && (
              <CTSTLGenerator
                filename={filename}
                email={email}
                credits={credits}
                setCredits={setCredits}
                isProcessed={isProcessed}
                processedImage={processedImage}
                overlayImage={overlayImage}
                colorList={colorList}
                extrusionValues={extrusionValues}
                setExtrusionValues={setExtrusionValues}
                projectRoot={projectRoot}
                setHasDownloaded={setHasDownloaded}
                hydratedBaseSvg={hydratedBaseSvg}
              />
            )}

            {/* --- MY STOREFRONT / MY SHOP (Tab 11) --- */}
            {activeMainTab === 11 && canAccessStoreAndProjects && (
              <MyStorefront
                email={email}
                username={usernamePart}
                tier={tier}
                credits={credits}
                setCredits={setCredits}
              />
            )}

            {/* --- My Projects --- */}
            {activeMainTab === 12 && canAccessStoreAndProjects && (
              <MyProjects
                username={email}
                onOpenProject={handleOpenProject}
                onCreditsUpdate={setCredits}
              />
            )}
          </main>
        </div>
      </div>

      {!isLoggedIn && (
        <div style={STYLES.fixedOverlayFull}>
          <LoginOverlay onLogin={user => {
            const incomingTier = user?.tier || '';
            const incomingNorm = String(incomingTier)
              .trim()
              .toLowerCase()
              .replace(/\s+/g, '_')
              .replace(/-+/g, '_');
            const incomingFlat = incomingNorm.replace(/_/g, '');

            const allowStoreProjects =
              incomingNorm === 'admin' || incomingFlat === 'admin' ||
              incomingNorm === 'all_access' || incomingFlat === 'allaccess' ||
              incomingNorm === 'patreon_access' || incomingFlat === 'patreonaccess';

            const allowTab12 = allowStoreProjects;

            setIsLoggedIn(true);
            setEmail(user.email);
            setCredits(user.credits);
            setTier(incomingTier);

            fetchMaintenanceStatus();
            setShowReleaseNotes(true);

            setActiveMainTab(allowTab12 ? 12 : 1);
            setActiveSubTab(1);

            setIsProcessed(false);
            setProcessedImage(null);
            setOverlayImage(null);
            setHasDownloaded(false);

            setCanEnterSubTab2(false);

            setShowCapitan(false);
            setCapitanImgSrc('/capitan.svg');
            setCapitanDisabled(sessionStorage.getItem(CAPITAN_DISABLED_KEY) === '1');
            capitanApi.onStartNewProject();
          }} />
        </div>
      )}

      {isLoggedIn && tier !== 'admin' && maintenance && (
        <div style={STYLES.maintenanceMask}>
          <img src="/Maintenance2.png" alt="Maintenance Mode" style={STYLES.maintenanceImg} />
        </div>
      )}

      {showReleaseNotes && (
        <ReleaseNotesOverlay visible={true} onClose={() => setShowReleaseNotes(false)} />
      )}

      {isLoggedIn && showPayPal && (
        <PayPalOverlay email={email} onClose={() => setShowPayPal(false)} setCredits={setCredits} />
      )}

      {/* Capitan overlay (bottom-left). */}
      {isLoggedIn && showCapitan && !capitanDisabled && (
        <div id="capitan-nudge" style={STYLES.capitanWrap}>
          <div style={STYLES.capitanBox}>
            <button
              onClick={dismissCapitan}
              aria-label="Close capitan"
              style={STYLES.capitanClose}
            >
              ×
            </button>

            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
              <AnimatedSvg
                src={capitanImgSrc}
                title="Capitan"
                style={STYLES.capitanImg}
              />
              <AnimatedSvg
                src={capitanActionSvgSrc}
                title="Capitan Action"
                style={{ height: capitanSvgHeight, width: 'auto' }}
              />
            </div>
          </div>

          <button onClick={disableCapitan} style={STYLES.nudgeBtn}>
            Don’t show again
          </button>
        </div>
      )}

      {/* Helper overlay (bottom-right). tick bubble above helper, X next to helper. BOTH clickable to open SupportTicketFormOverlay */}
      {isLoggedIn && showClickNudge && !nudgeDisabled && (
        <div id="click-nudge" style={STYLES.clickNudgeWrap}>
          <div style={STYLES.clickNudgeStack}>
            {/* tick bubble (clickable) */}
            <div
              role="button"
              tabIndex={0}
              onClick={openTicketOverlay}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') openTicketOverlay();
              }}
              style={{ cursor: 'pointer' }}
            >
              <AnimatedSvg
                src="/tick_cowboy_bub.svg"
                title="Tick Cowboy"
                style={STYLES.tickCowboyImg}
              />
            </div>

            {/* helper row: [helper svg clickable] [x button] */}
            <div style={STYLES.helperRow}>
              <div
                role="button"
                tabIndex={0}
                onClick={openTicketOverlay}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') openTicketOverlay();
                }}
                style={{ cursor: 'pointer' }}
              >
                <AnimatedSvg
                  src="/helper.svg"
                  title="Helper"
                  style={STYLES.helperImg}
                />
              </div>

              <button
                onClick={dismissNudge}
                aria-label="Close helper"
                style={STYLES.clickNudgeCloseInline}
                type="button"
              >
                ×
              </button>
            </div>
          </div>

          <button onClick={disableNudge} style={STYLES.nudgeBtn}>
            Don’t show again
          </button>
        </div>
      )}

      {isLoggedIn && credits === 0 && (
        <button onClick={() => setShowPayPal(true)} className="flashing-text" style={STYLES.paypalBtn}>
          Buy More Credits
        </button>
      )}

      {/* ✅ Ensure ticket overlay is always above helper/tick UI */}
      {isLoggedIn && showTicketForm && (
        <div style={{ ...STYLES.fixedOverlayFull, zIndex: 20000 }}>
          <SupportTicketFormOverlay
            email={email}
            defaultFilename={filename}
            onClose={() => setShowTicketForm(false)}
            onSubmitted={() => { alert('Thanks! Your ticket was submitted.'); }}
          />
        </div>
      )}
    </div>
  );
}
